import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { lookupUserIdsOrEmails, collectAttendance } from '../api';
import { IUserDetail } from 'react-scale/components/WebinarSessions/types';
import { Button, Icon, Typography, SegmentedButtons } from '@scale/scaleui';
import SelectScaler from 'react-scale/corp/components/UI/SelectScaler';
import { UserIdsOrEmailsTextInput } from './UserIdsOrEmailsTextInput';
import _ from 'lodash';
import moment from 'moment-timezone';
import 'moment-duration-format';
import { toast } from 'react-toastify';
import FileSaver from 'file-saver';
import { stringify } from 'csv-stringify/sync';

interface EditAttendanceProps {
  session: any;
  attendees: string[];
  setAttendees: React.Dispatch<React.SetStateAction<string[]>>;
  disabled?: boolean;
}

enum AddAttendeesMode {
  Search = 'Search',
  List = 'List',
  RunSFN = 'Run SFN',
}

const ATTENDANCE_CSV_HEADERS = [
  'user_id',
  'user_name',
  'email',
  'zoom_name',
  'total_duration',
  'zoom_attendances',
];

export const EditAttendance: React.FC<EditAttendanceProps> = ({
  session,
  attendees,
  setAttendees,
  disabled,
}) => {
  const attendanceSyncedAt = session?.attendanceSyncedAt;
  const zoomAttendees = session?.zoomAttendees;
  const [addAttendeesMode, setAddAttendeesMode] = useState<AddAttendeesMode>(
    AddAttendeesMode.Search,
  );
  const [attendedUsers, setAttendedUsers] = useState<IUserDetail[]>([]);
  const [collectingAttendance, setCollectingAttendance] = useState<boolean>(false);
  const [downloadingCSV, setDownloadingCSV] = useState<boolean>(false);

  const attendeesIds = useMemo(() => {
    const ids = attendedUsers.map(host => host.id);
    setAttendees(ids);
    return ids;
  }, [attendedUsers, setAttendees]);

  useEffect(() => {
    if (attendees?.length && session._id)
      (async () => {
        const users = await lookupUserIdsOrEmails(attendees);
        setAttendedUsers(users);
      })();
  }, [session._id]);

  const userIdsOrEmailsTextInputCallback = useCallback(
    (users: IUserDetail[]) => {
      setAttendees((a: string[]) => _.uniq([...a, ...users.map(u => u.id)]));
    },
    [setAttendees],
  );

  const addUser = useCallback(
    (user: any) => {
      if (!attendeesIds.includes(user.value))
        setAttendedUsers(aU => [...aU, { id: user.value, email: user.label }]);
    },
    [attendeesIds],
  );

  const removeUser = useCallback((id: string | null) => {
    if (id) setAttendedUsers(aU => aU.filter(u => u.id !== id));
  }, []);

  const handleCollectAttendance = useCallback(async () => {
    setCollectingAttendance(true);
    await collectAttendance(session._id);
    setCollectingAttendance(false);
  }, [session]);

  const allAttendeesMap: { [email: string]: any[] } = useMemo(() => {
    const map: Record<string, any[]> = attendedUsers
      .map((aU: IUserDetail) => aU.email.replace('+outlier', '').replace('+worker@', '@'))
      .reduce((a: Record<string, any[]>, curr: string) => ({ ...a, [curr]: [] }), {});
    const secondaryEmails = attendedUsers
      .map((aU: IUserDetail) => (aU.secondaryEmail ?? '').toLowerCase())
      .filter(e => !!e);
    for (const zA of zoomAttendees) {
      if (map[zA.email.toLowerCase()]) map[zA.email.toLowerCase()].push(zA);
      else if (secondaryEmails.includes(zA.email.toLowerCase())) {
        const user = attendedUsers.find(aU => aU.secondaryEmail === zA.email.toLowerCase());
        if (user) map[user.email.replace('+outlier', '').replace('+worker@', '@')]?.push(zA);
      } else if (map[`_zA_${zA.email}`]) map[`_zA_${zA.email}`].push(zA);
      else map[`_zA_${zA.email || zA.name}`] = [zA];
    }
    return map;
  }, [zoomAttendees, attendedUsers]);

  const attendedUsersWithZoomAttendance: { [email: string]: any[] } = useMemo(
    () =>
      Object.keys(allAttendeesMap)
        .filter(key => !key.startsWith('_zA_'))
        .reduce((a, c) => ({ ...a, [c]: allAttendeesMap[c] }), {}),
    [allAttendeesMap],
  );

  const zoomAttendeesWithNoUserMatch: { [email: string]: any[] } = useMemo(
    () =>
      Object.keys(allAttendeesMap)
        .filter(key => key.startsWith('_zA_'))
        .reduce((a, c) => ({ ...a, [c]: allAttendeesMap[c] }), {}),
    [allAttendeesMap],
  );

  const handleDownloadCSV = useCallback(() => {
    setDownloadingCSV(true);
    try {
      const data = Object.entries(allAttendeesMap).map(([email, zAs]) => [
        attendedUsers.find(
          aU => aU.email.replace('+outlier', '').replace('+worker@', '@') === email,
        )?.id ?? '',
        attendedUsers.find(
          aU => aU.email.replace('+outlier', '').replace('+worker@', '@') === email,
        )?.name ?? '',
        email,
        zAs[0]?.name ?? '',
        moment
          .duration(
            zAs.reduce((a, curr) => a + curr.duration, 0),
            's',
          )
          .format('h[h] m[m] s[s]'),
        JSON.stringify(zAs),
      ]);
      const csv = stringify([ATTENDANCE_CSV_HEADERS, ...data]);
      const blob = new Blob([csv], { type: 'text/csv' });
      const fileName = `webinar_session_attendance_${session._id}.csv`;
      FileSaver.saveAs(blob, fileName);
    } catch (error) {
      toast(`Failed to download attendance CSV: ${error.message}`, {
        type: 'error',
        autoClose: 2000,
        position: 'top-right',
      });
    } finally {
      setDownloadingCSV(false);
    }
  }, [attendedUsers, allAttendeesMap, session._id]);

  return (
    <>
      <div className="flex gap-1 items-baseline">
        <Typography.Subtitle3 className="text-neutral-600 mb-1">
          {disabled ? '' : 'Edit '}Attendance
        </Typography.Subtitle3>
        {attendanceSyncedAt && (
          <Typography.Paragraph3 className="text-neutral-600 mb-1">
            (Last synced {moment(attendanceSyncedAt).format('MMM D, YYYY h:mm A')})
          </Typography.Paragraph3>
        )}
        <Button
          disabled={downloadingCSV}
          className="ml-auto"
          variant="secondary"
          onClick={handleDownloadCSV}
        >
          Download Attendance CSV
        </Button>
      </div>
      <div className="flex w-full space-between gap-4">
        <div className="flex flex-col">
          <div className="flex gap-2">
            <Typography.Paragraph3 className="text-neutral-600 w-[384px] font-semibold">
              User Attendees ({Object.keys(attendedUsersWithZoomAttendance).length})
            </Typography.Paragraph3>
            <Typography.Paragraph3 className="text-neutral-600 w-[128px] font-semibold">
              Attendance Duration
            </Typography.Paragraph3>
          </div>
          {Object.entries(attendedUsersWithZoomAttendance).map(([email, zAs]) => (
            <div className="flex gap-2" key={email}>
              <Typography.Paragraph3 className="flex items-center w-[384px]">
                • {email} ({zAs[0]?.name})
                {!disabled && (
                  <Icon
                    size="sm"
                    icon={['far', 'trash']}
                    onClick={() =>
                      removeUser(
                        attendedUsers.find(
                          aU => aU.email.replace('+outlier', '').replace('+worker@', '@') === email,
                        )?.id ?? null,
                      )
                    }
                  />
                )}
              </Typography.Paragraph3>
              <Typography.Paragraph3 className="flex items-center">
                {!!zAs.length &&
                  moment
                    .duration(
                      zAs.reduce((a, curr) => a + curr.duration, 0),
                      's',
                    )
                    .format('h[h] m[m] s[s]')}
                {!!zAs.length && (
                  <Icon
                    size="sm"
                    icon={['far', 'circle-info']}
                    tooltip={zAs
                      .map(
                        zA =>
                          `Status: ${zA.status}, Join: ${moment(zA.joinTime).format(
                            'hh:mm:ss a',
                          )}, Leave: ${moment(zA.leaveTime).format(
                            'hh:mm:ss A',
                          )}, Duration: ${moment
                            .duration(zA.duration, 's')
                            .format('h[h] m[m] s[s]')}`,
                      )
                      .join('\n')}
                    tooltipProps={{ side: 'left', contentClassName: 'whitespace-pre' }}
                  />
                )}
                {!zAs.length && 'No Zoom Attendance Linked'}
              </Typography.Paragraph3>
            </div>
          ))}
          <div className="flex gap-2 my-1">
            <Typography.Paragraph3 className="text-neutral-600 w-[512px] font-semibold">
              Zoom Attendees (No User Match or Insufficient Attendance Duration) (
              {Object.keys(zoomAttendeesWithNoUserMatch).length})
            </Typography.Paragraph3>
          </div>
          {Object.entries(zoomAttendeesWithNoUserMatch).map(([email, zAs]) => (
            <div className="flex gap-2" key={email}>
              <Typography.Paragraph3 className="flex items-center w-[384px]">
                • {email.substring(4)} ({zAs[0]?.name})
              </Typography.Paragraph3>
              <Typography.Paragraph3 className="flex items-center">
                {moment
                  .duration(
                    zAs.reduce((a, curr) => a + curr.duration, 0),
                    's',
                  )
                  .format('h[h] m[m] s[s]')}
                {!!zAs.length && (
                  <Icon
                    size="sm"
                    icon={['far', 'circle-info']}
                    tooltip={zAs
                      .map(
                        zA =>
                          `Status: ${zA.status}, Join: ${moment(zA.joinTime).format(
                            'hh:mm:ss a',
                          )}, Leave: ${moment(zA.leaveTime).format(
                            'hh:mm:ss A',
                          )}, Duration: ${moment
                            .duration(zA.duration, 's')
                            .format('h[h] m[m] s[s]')}`,
                      )
                      .join('\n')}
                    tooltipProps={{ side: 'left', contentClassName: 'whitespace-pre' }}
                  />
                )}
              </Typography.Paragraph3>
            </div>
          ))}
        </div>
        {!disabled && (
          <div className="flex flex-col gap-1">
            <Typography.Paragraph3 className="text-neutral-600">
              Add Attendees
            </Typography.Paragraph3>
            <SegmentedButtons
              selected={addAttendeesMode}
              onSelect={(h: string) => setAddAttendeesMode(h as AddAttendeesMode)}
              buttons={Object.values(AddAttendeesMode).map(m => ({ id: m, label: m }))}
              className="mb-1"
            />
            {addAttendeesMode === AddAttendeesMode.Search && (
              <SelectScaler
                placeholder="Search for a contributor by email"
                onChange={addUser}
                filterOption={() => true}
                className="w-[280px]"
              />
            )}
            {addAttendeesMode === AddAttendeesMode.List && (
              <UserIdsOrEmailsTextInput
                setUsers={setAttendedUsers}
                callbackFn={userIdsOrEmailsTextInputCallback}
              />
            )}
            {addAttendeesMode === AddAttendeesMode.RunSFN && (
              <Button
                variant="neutral"
                onClick={handleCollectAttendance}
                disabled={collectingAttendance}
                className="mr-auto"
              >
                Run Attendance Collection
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  );
};
